import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

/**
 * Three-dot row menu.
 *
 * Rendered in a portal with fixed positioning so it is never clipped by a
 * scrolling table, and it flips above the trigger when there is not enough
 * room below.
 *
 * items: [{ label, icon, onClick, variant: 'default' | 'danger', disabled }]
 */
const ActionMenu = ({ items = [], label = 'Actions', align = 'right', width = 176 }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, openUp: false });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const place = () => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const estimatedHeight = Math.min(items.filter(Boolean).length * 38 + 12, 320);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estimatedHeight && rect.top > estimatedHeight;

    const left =
      align === 'right' ? Math.max(8, rect.right - width) : Math.min(rect.left, window.innerWidth - width - 8);

    setCoords({
      top: openUp ? rect.top - estimatedHeight - 6 : rect.bottom + 6,
      left,
      openUp,
    });
  };

  useLayoutEffect(() => {
    if (open) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (
        !buttonRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    // Closing on scroll keeps the fixed-position menu from drifting.
    const onScroll = () => setOpen(false);
    const onResize = () => setOpen(false);

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const visibleItems = items.filter(Boolean);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: 'fixed', top: coords.top, left: coords.left, width }}
            className="z-[100] overflow-hidden rounded-lg border border-ink-200 bg-white py-1 shadow-card-hover animate-scale-in"
          >
            {visibleItems.length === 0 && (
              <p className="px-3 py-2 text-xs text-ink-400">No actions</p>
            )}

            {visibleItems.map((item, index) => {
              const Icon = item.icon;
              const isDanger = item.variant === 'danger';
              return (
                <button
                  key={`${item.label}-${index}`}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    setOpen(false);
                    item.onClick?.();
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    isDanger
                      ? 'text-red-600 hover:bg-red-50'
                      : 'text-ink-700 hover:bg-ink-100'
                  }`}
                >
                  {Icon ? <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" /> : null}
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default ActionMenu;
